import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import {
  ruleLifecycleApi,
  RULE_LIFECYCLE_FLAG,
  type RuleLifecycleFamily,
  type RuleLifecycleRow,
  type RuleLifecycleStatus,
} from '../api/ruleLifecycle'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Callout,
  Card,
  CardHeader,
  DataTable,
  OverflowMenu,
  StatusPill,
  usePrompt,
  useSnackbar,
  type PillTone,
} from './ui'

/**
 * C-16: il ciclo di vita uniforme delle regole di una famiglia (stato, versione, validita', motivazione e passi
 * ammessi). Compare solo col flag `regole.ciclo_vita` acceso; ogni passo chiede la motivazione. Spento, la pagina
 * resta quella di prima.
 */
const TONES: Record<RuleLifecycleStatus, PillTone> = {
  Bozza: 'neutral',
  Test: 'info',
  Simulazione: 'info',
  Approvazione: 'warning',
  Pubblicata: 'primary',
  Attiva: 'success',
  Archiviata: 'neutral',
}

const ACTION_LABELS: Record<RuleLifecycleStatus, string> = {
  Bozza: 'Riporta in bozza',
  Test: 'Passa al test',
  Simulazione: 'Passa alla simulazione',
  Approvazione: 'Chiedi approvazione',
  Pubblicata: 'Pubblica',
  Attiva: 'Attiva',
  Archiviata: 'Archivia',
}

function day(iso: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function validity(r: RuleLifecycleRow): string {
  if (!r.validFrom && !r.validTo) return 'Sempre'
  if (r.validFrom && r.validTo) return `${day(r.validFrom)} – ${day(r.validTo)}`
  return r.validFrom ? `Dal ${day(r.validFrom)}` : `Fino al ${day(r.validTo)}`
}

export function RuleLifecyclePanel({ family, onChanged }: { family: RuleLifecycleFamily; onChanged?: () => void }) {
  const enabled = useFeatureFlag(RULE_LIFECYCLE_FLAG)
  const prompt = usePrompt()
  const snackbar = useSnackbar()
  const [rows, setRows] = useState<RuleLifecycleRow[]>([])
  const [error, setError] = useState('')
  const [forbidden, setForbidden] = useState(false)

  const load = useCallback(async () => {
    setError('')
    try {
      setRows(await ruleLifecycleApi.list(family))
    } catch (e) {
      // R3-29: il ciclo di vita e' solo per gli amministratori; per gli altri il pannello non compare.
      if (e instanceof ApiError && e.status === 403) { setForbidden(true); return }
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del ciclo di vita')
    }
  }, [family])

  useEffect(() => {
    if (enabled) void load()
  }, [enabled, load])

  if (!enabled || forbidden) return null

  // R3-29: simulazione a secco (ultimi tre mesi) nelle fasi Test e Simulazione; senza una simulazione salvata in fase
  // Simulazione la regola non va in approvazione.
  async function simulate(r: RuleLifecycleRow) {
    try {
      const s = await ruleLifecycleApi.simulate(family, r.id)
      snackbar.show({ message: `Simulazione salvata (${s.phase}): ${s.summary}` })
      await load()
      onChanged?.()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Simulazione non riuscita', tone: 'error' })
    }
  }

  async function move(r: RuleLifecycleRow, to: RuleLifecycleStatus) {
    const reason = await prompt({
      title: `${ACTION_LABELS[to]}: ${r.name}`,
      message: to === 'Pubblicata'
        ? 'La pubblicazione va fatta da un amministratore diverso da chi ha chiesto l\'approvazione.'
        : undefined,
      label: 'Motivazione',
      required: true,
      multiline: true,
      confirmLabel: ACTION_LABELS[to],
      destructive: to === 'Archiviata',
    })
    if (reason === null || reason.trim() === '') return
    try {
      await ruleLifecycleApi.transition(family, r.id, { to, reason: reason.trim() })
      snackbar.show({ message: `Regola "${r.name}": ${to}` })
      await load()
      onChanged?.()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Operazione non riuscita', tone: 'error' })
    }
  }

  return (
    <Card padding="none">
      <CardHeader
        title="Ciclo di vita"
        subtitle={rows[0]?.managedElsewhere
          ?? "Una regola diventa operativa solo dopo la pubblicazione e va in approvazione solo con una simulazione salvata. Per cambiare una regola pubblicata clonala: la copia e' la versione successiva e, quando entra in vigore, archivia la precedente."}
        icon="published_with_changes"
      />
      {error && <Callout tone="error">{error}</Callout>}
      <DataTable variant="plain" aria-label="Ciclo di vita delle regole">
        <thead>
          <tr>
            <th>Regola</th>
            <th>Versione</th>
            <th>Stato</th>
            <th>Validità</th>
            <th>Motivazione</th>
            <th aria-label="Azioni" />
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>v{r.version}{r.previousVersionId ? ` (da #${r.previousVersionId})` : ''}</td>
              <td><StatusPill tone={TONES[r.status]}>{r.status}</StatusPill></td>
              <td>{validity(r)}</td>
              <td>{r.reason ?? ''}</td>
              <td>
                {(r.next.length > 0 || r.canSimulate) && (
                  <OverflowMenu
                    size="sm"
                    aria-label={`Passi del ciclo di ${r.name}`}
                    items={[
                      ...(r.canSimulate ? [{ label: 'Simula (ultimi 3 mesi)', onSelect: () => void simulate(r) }] : []),
                      ...r.next.map(to => ({ label: ACTION_LABELS[to], onSelect: () => void move(r, to) })),
                    ]}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  )
}
